import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Multiselect, { MultiselectProps } from '@cloudscape-design/components/multiselect';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { beginLogin } from '../auth';
import { useCapabilities } from '../capabilities';
import { getConfig } from '../config';
import { SUPERREADER_SCOPE } from '../constants';
import { orderScopes, scopeLabel } from '../utils/format';
import { withSupport } from '../utils/support';
import { NotFoundError, UnauthorizedError, updateReportScopes } from '../api';

function editableScopes(scopes: string[] | undefined): string[] {
  return (scopes ?? []).filter((scope) => scope !== SUPERREADER_SCOPE);
}

export interface ScopeEditorProps {
  reportId: string;
  scopes?: string[];
  onSaved: (scopes: string[]) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function ScopeEditor({ reportId, scopes, onSaved, onDirtyChange }: ScopeEditorProps) {
  const { canManageScopes } = useCapabilities();
  const [editing, setEditing] = useState(false);
  const [selection, setSelection] = useState<readonly MultiselectProps.Option[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const options = useMemo<MultiselectProps.Option[]>(
    () =>
      orderScopes(getConfig().scopes.filter((scope) => scope !== SUPERREADER_SCOPE)).map((scope) => ({
        label: scopeLabel(scope),
        value: scope,
      })),
    [],
  );

  const initial = useMemo(() => editableScopes(scopes), [scopes]);

  const dirty = useMemo(() => {
    const current = selection.map((option) => option.value!).filter(Boolean);
    if (current.length !== initial.length) return true;
    const initialSet = new Set(initial);
    return current.some((scope) => !initialSet.has(scope));
  }, [selection, initial]);

  useEffect(() => {
    onDirtyChange?.(editing && dirty);
  }, [editing, dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const beginEdit = useCallback(() => {
    setSelection(initial.map((scope) => ({ label: scopeLabel(scope), value: scope })));
    setError(undefined);
    setEditing(true);
  }, [initial]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setError(undefined);
  }, []);

  const save = useCallback(async () => {
    const next = selection.map((option) => option.value!).filter(Boolean);
    if (next.length === 0) {
      setError('Choose at least one scope.');
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const result = await updateReportScopes(reportId, next);
      setEditing(false);
      onSaved(result.scopes);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await beginLogin();
        return;
      }
      setError(
        withSupport(
          err instanceof NotFoundError
            ? 'Report not found, or outside your access scopes.'
            : err instanceof Error
              ? err.message
              : 'Could not update scopes.',
        ),
      );
    } finally {
      setSaving(false);
    }
  }, [reportId, selection, onSaved]);

  if (!canManageScopes) return null;

  return (
    <div>
      <Box variant="awsui-key-label">Scopes</Box>
      {editing ? (
        <SpaceBetween size="xs">
          <Multiselect
            selectedOptions={selection}
            onChange={({ detail }) => setSelection(detail.selectedOptions)}
            options={options}
            placeholder="Choose scopes"
            ariaLabel="Edit report scopes"
            disabled={saving}
          />
          {error && <StatusIndicator type="error">{error}</StatusIndicator>}
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="primary" onClick={() => void save()} loading={saving}>
              Save scopes
            </Button>
            <Button onClick={cancelEdit} disabled={saving}>
              Cancel
            </Button>
          </SpaceBetween>
        </SpaceBetween>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <div data-testid="detail-scopes">
            {initial.length > 0 ? orderScopes(initial).map(scopeLabel).join(', ') : '—'}
          </div>
          <Button iconName="edit" onClick={beginEdit}>
            Edit scopes
          </Button>
        </div>
      )}
    </div>
  );
}
